// src/components/FuelInvoicePDF.js
import { jsPDF } from 'jspdf';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const TX_FUEL_TAX   = 0.20;
const FED_EXCISE    = 0.194;

const safe = (v) => (v != null && v !== undefined ? String(v) : '');
const usd  = (n)  => '$' + Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const num  = (n)  => Number(n || 0);

const fuelLabel = (t) => ({ JetA: 'Jet-A', '100LL': '100LL Avgas', JetA_plus: 'Jet-A+' }[t] || safe(t));

const fmtDate = (ts) => {
  if (!ts) return '';
  try { return new Date(ts).toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric', timeZone:'America/Chicago' }); }
  catch { return ''; }
};

const fmtDateTime = (ts) => {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString('en-US', { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit', timeZone:'America/Chicago' }) + ' CT'; }
  catch { return ''; }
};

const loadLogoAsJpeg = (url) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const c = document.createElement('canvas');
      c.width  = img.naturalWidth  || 200;
      c.height = img.naturalHeight || 200;
      c.getContext('2d').drawImage(img, 0, 0);
      resolve(c.toDataURL('image/jpeg', 0.9));
    } catch { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = url;
});

export const generateFuelInvoice = async (txId, mode = 'download') => {

  // ── Fetch data ────────────────────────────────────────────────────────────
  const [companyRes, txRes] = await Promise.all([
    fetch(`${API_BASE}/company/settings`).then(r => r.json()).catch(() => ({})),
    fetch(`${API_BASE}/fuel/transactions/${txId}`).then(r => r.json()).catch(() => ({})),
  ]);
  const company = companyRes || {};
  const tx      = txRes      || {};

  let aircraft = {};
  if (tx.aircraft_id) {
    aircraft = await fetch(`${API_BASE}/aircraft/${tx.aircraft_id}`)
      .then(r => r.json()).catch(() => ({}));
  }

  // ── Calculations ──────────────────────────────────────────────────────────
  const gallons     = num(tx.gallons);
  const ppu         = num(tx.price_per_gallon);
  const subtotal    = gallons * ppu;
  const txTax       = gallons * TX_FUEL_TAX;
  const fedTax      = gallons * FED_EXCISE;
  const salesRate   = num(company.tax_rate) / 100;
  const salesTax    = subtotal * salesRate;
  const surRate     = num(company.fuel_surcharge_percent) / 100;
  const surcharge   = subtotal * surRate;
  const total       = subtotal + txTax + fedTax + salesTax + surcharge;

  // ── PDF setup ─────────────────────────────────────────────────────────────
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const W = 612, H = 792, M = 45;
  let y = 0;

  const C = {
    blue:   [30,  64,  175],
    accent: [59,  130, 246],
    white:  [255, 255, 255],
    dark:   [17,  24,  39],
    mid:    [75,  85,  99],
    light:  [156, 163, 175],
    row:    [243, 244, 246],
    green:  [22,  163, 74],
    line:   [229, 231, 235],
  };

  const setFont  = (style, size, color) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(...(color || C.dark));
  };

  // ── Header band ───────────────────────────────────────────────────────────
  doc.setFillColor(...C.blue);
  doc.rect(0, 0, W, 115, 'F');

  // Logo
  let logoX = M;
  if (company.logo_url) {
    const jpeg = await loadLogoAsJpeg(`${API_BASE}${company.logo_url}`);
    if (jpeg) {
      try {
        doc.addImage(jpeg, 'JPEG', M, 16, 72, 72);
        logoX = M + 82;
      } catch (e) { console.warn('logo skip', e); }
    }
  }

  // Company name + info
  setFont('bold', 18, C.white);
  doc.text(safe(company.name) || 'FBO', logoX, 40);

  setFont('normal', 8, C.white);
  const addr = [
    safe(company.address),
    safe(company.city),
    safe(company.state),
    safe(company.zip_code),
  ].filter(Boolean).join(', ');
  if (addr) doc.text(addr, logoX, 54);
  if (company.phone) doc.text(safe(company.phone), logoX, 66);
  if (company.email) doc.text(safe(company.email), logoX, 78);
  if (company.icao_code) doc.text('ICAO: ' + safe(company.icao_code), logoX, 90);

  // INVOICE title
  setFont('bold', 26, C.white);
  doc.text('INVOICE', W - M, 46, { align: 'right' });
  setFont('normal', 8, C.white);
  const txNum = safe(tx.transaction_number) || safe(txId).slice(0, 8).toUpperCase();
  doc.text('# ' + txNum,            W - M, 62, { align: 'right' });
  doc.text(fmtDate(tx.timestamp),   W - M, 74, { align: 'right' });
  doc.text(fmtDateTime(tx.timestamp), W - M, 86, { align: 'right' });

  y = 130;

  // ── Info boxes ────────────────────────────────────────────────────────────
  const bw = (W - M * 2 - 10) / 2;
  const bh = 100;

  const drawBox = (x, title, lines) => {
    doc.setFillColor(...C.row);
    doc.roundedRect(x, y, bw, bh, 4, 4, 'F');
    doc.setFillColor(...C.accent);
    doc.roundedRect(x, y, bw, 20, 4, 4, 'F');
    doc.rect(x, y + 10, bw, 10, 'F');
    setFont('bold', 8, C.white);
    doc.text(title, x + 10, y + 14);
    lines.forEach((ln, i) => {
      if (!ln) return;
      setFont(i === 0 ? 'bold' : 'normal', i === 0 ? 10 : 8, i === 0 ? C.dark : C.mid);
      doc.text(safe(ln), x + 10, y + 32 + i * 14);
    });
  };

  const acDesc = [
    aircraft.manufacturer && aircraft.model ? aircraft.manufacturer + ' ' + aircraft.model : null,
    aircraft.aircraft_type ? 'Type: ' + aircraft.aircraft_type : null,
    aircraft.year_manufactured ? 'Year: ' + aircraft.year_manufactured : null,
    aircraft.serial_number ? 'S/N: ' + aircraft.serial_number : null,
  ].filter(Boolean);

  drawBox(M,         'BILL TO',  [tx.owner_name, tx.staff_name ? 'Served by: ' + tx.staff_name : 'Self-service']);
  drawBox(M + bw + 10, 'AIRCRAFT', [tx.tail_number, ...acDesc]);

  y += bh + 18;

  // ── Table header ──────────────────────────────────────────────────────────
  doc.setFillColor(...C.blue);
  doc.rect(M, y, W - M * 2, 22, 'F');
  setFont('bold', 8, C.white);
  doc.text('DESCRIPTION',  M + 10,      y + 15);
  doc.text('QTY (GAL)',    M + 295,     y + 15, { align: 'right' });
  doc.text('UNIT PRICE',   M + 390,     y + 15, { align: 'right' });
  doc.text('AMOUNT',       W - M - 10,  y + 15, { align: 'right' });
  y += 22;

  const row = (desc, qty, unit, amt, shade) => {
    const rh = 26;
    if (shade) { doc.setFillColor(...C.row); doc.rect(M, y, W - M * 2, rh, 'F'); }
    setFont('normal', 8, C.dark);
    doc.text(safe(desc), M + 10,     y + 17);
    if (qty)  doc.text(safe(qty),  M + 295,    y + 17, { align: 'right' });
    if (unit) doc.text(safe(unit), M + 390,    y + 17, { align: 'right' });
    doc.text(safe(amt),  W - M - 10, y + 17, { align: 'right' });
    y += rh;
  };

  row(fuelLabel(tx.fuel_type) + ' — Aviation Fuel',
      gallons.toFixed(2) + ' gal', usd(ppu) + '/gal', usd(subtotal), false);

  if (surcharge > 0)
    row('Fuel Surcharge (' + num(company.fuel_surcharge_percent) + '%)',
        '', '', usd(surcharge), true);

  // Tax section label
  doc.setDrawColor(...C.line); doc.setLineWidth(0.5);
  doc.line(M, y + 4, W - M, y + 4); y += 14;
  setFont('bold', 8, C.mid);
  doc.text('TAXES & FEES (TEXAS / FEDERAL)', M + 10, y + 10); y += 20;

  row('Texas Aviation Fuel Tax — Tex. Tax Code §162.502 ($' + TX_FUEL_TAX.toFixed(3) + '/gal)',
      gallons.toFixed(2) + ' gal', '$' + TX_FUEL_TAX.toFixed(3) + '/gal', usd(txTax), false);

  row('Federal Fuel Excise Tax — 26 U.S.C. §4091 ($' + FED_EXCISE.toFixed(3) + '/gal)',
      gallons.toFixed(2) + ' gal', '$' + FED_EXCISE.toFixed(3) + '/gal', usd(fedTax), true);

  if (salesTax > 0)
    row('Texas Sales Tax (' + num(company.tax_rate) + '%)', '', '', usd(salesTax), false);

  y += 10;

  // ── Totals ────────────────────────────────────────────────────────────────
  const tw = 210, tx2 = W - M - tw;
  doc.setDrawColor(...C.line); doc.line(tx2, y, W - M, y); y += 4;

  const totRow = (lbl, val, bold, bg) => {
    const rh = 24;
    if (bg) { doc.setFillColor(...bg); doc.rect(tx2, y, tw, rh, 'F'); }
    setFont(bold ? 'bold' : 'normal', bold ? 10 : 8, bg ? C.white : C.dark);
    doc.text(safe(lbl), tx2 + 8,   y + rh / 2 + 3);
    doc.text(safe(val), W - M - 8, y + rh / 2 + 3, { align: 'right' });
    y += rh;
  };

  totRow('Subtotal',         usd(subtotal),  false, null);
  if (surcharge > 0) totRow('Surcharge',     usd(surcharge), false, null);
  totRow('TX Fuel Tax',      usd(txTax),     false, null);
  totRow('Fed. Excise Tax',  usd(fedTax),    false, null);
  if (salesTax > 0) totRow('Sales Tax',      usd(salesTax),  false, null);
  doc.setDrawColor(...C.blue); doc.setLineWidth(1);
  doc.line(tx2, y, W - M, y); y += 2;
  totRow('TOTAL DUE',        usd(total),     true,  C.blue);

  y += 16;

  // Status badge
  doc.setFillColor(...(tx.invoice_id ? C.green : [202, 138, 4]));
  doc.roundedRect(M, y, 155, 24, 4, 4, 'F');
  setFont('bold', 8, C.white);
  doc.text(tx.invoice_id ? 'LINKED TO INVOICE' : 'AWAITING INVOICE', M + 77, y + 16, { align: 'center' });

  // ── Legal footer ──────────────────────────────────────────────────────────
  y = H - 108;
  doc.setDrawColor(...C.line); doc.setLineWidth(0.5); doc.line(M, y, W - M, y); y += 10;
  setFont('bold', 7, C.mid);
  doc.text('REGULATORY COMPLIANCE NOTICE', M, y); y += 11;
  setFont('normal', 6.5, C.light);

  const legal = [
    'Aviation fuel taxes collected per Texas Tax Code Chapter 162 and 26 U.S.C. §4091.',
    'TX Aviation Fuel Tax: $' + TX_FUEL_TAX.toFixed(3) + '/gal (Tex. Tax Code §162.502).   Federal Excise Tax: $' + FED_EXCISE.toFixed(3) + '/gal (26 U.S.C. §4091).',
    'This document is a fuel receipt and tax invoice under Texas and Federal regulations. Retain for your records.',
    safe(company.name) + ' — Licensed Aviation Fuel Dealer. ICAO: ' + safe(company.icao_code) + '. ' + addr,
  ];
  legal.forEach(ln => {
    const wrapped = doc.splitTextToSize(ln, W - M * 2);
    doc.text(wrapped, M, y);
    y += wrapped.length * 9;
  });

  setFont('italic', 6.5, C.light);
  doc.text('Generated ' + new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }) + ' CT — Portalith FBO',
    W / 2, H - 16, { align: 'center' });

  // ── Output ────────────────────────────────────────────────────────────────
  const fname = 'FuelInvoice_' + txNum + '_' + new Date().toISOString().slice(0, 10) + '.pdf';
  if (mode === 'preview') {
    window.open(URL.createObjectURL(doc.output('blob')), '_blank');
  } else {
    doc.save(fname);
  }
};